<template>
  <v-app class="vue3">
    <v-main>
      <navigation-drawer v-if="logged" :is-msp-route="isMspRoute" />
      <app-bar
        v-if="logged && workspace.workspaceId"
        :app-title="appTitle"
        :is-msp-route="isMspRoute"
      />
      <v-container fluid :class="{ 'pa-0 h-100': !logged }">
        <router-view />
      </v-container>
      <setup-hub />
    </v-main>
    <CoroDialog
      :config="dialogConfig"
      :value="showDialog"
      v-if="showDialog"
      @onClose="closeDialog"
    />
    <CoroSnackbar />
    <floating-go-back-button
      v-if="logged"
      :show="shouldShowGoBackButton()"
      @go-back="goBackToSecurityGaps"
      @dismiss="clearNavigationState"
    />
  </v-app>
</template>

<script lang="ts">
import AppBar from "@/components/AppBar.vue";
import CoroSnackbar from "@/components/CoroSnackbar.vue";
import CoroDialog from "@/components/modals/CoroDialog.vue";
import FloatingGoBackButton from "@/components/FloatingGoBackButton.vue";

import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useErrorsStore } from "@/_store/errors.module";
import { SnackbarTypes, useSnackbarStore } from "@/_store/snackbar.module";
import { useDialogsStore } from "@/_store/dialogs.module";
import { storeToRefs } from "pinia";
import { useMyAccountStore } from "@/_store/my-account.module";
import {
  dialogsConfigConstructor,
  extractCoroSubdomain,
  getSelectedAddons,
  getSelectedModules,
  handleFaviconAndTitle,
  isMspPortalRoute,
} from "@/_helpers/utils";
import ErrorModal from "@/components/modals/ErrorModal.vue";
import { ModalWidth } from "@/constants/modals";
import { useAccountStore } from "@/_store/account.module.ts";
import NavigationDrawer from "@/components/NavigationDrawer.vue";
import { useRoute } from "vue-router";
import { RouteName } from "@/constants/routes";
import { i18n } from "@/plugins/i18n";
import moment from "moment/moment";
import { WorkspaceType } from "@/constants/workplaces";
import { useLocale } from "vuetify";
import { useRedirectsStore } from "@/_store/redirects.module.ts";
import TrialStartedModal from "@/components/modals/TrialStartedModal.vue";
import { TutorialAction } from "@/constants/tutorial.ts";
import { useTutorialStore } from "@/_store/tutorial.module.ts";
import { useGtm } from "@gtm-support/vue-gtm";
import { GtmAction } from "@/constants/gtm.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useSecurityGapsNavigation } from "@/composables/useSecurityGapsNavigation.ts";
import { useAiAssistantStore } from "@/_store/ai-assistant.module.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import { AppContext } from "@/types.ts";
import SetupHub from "@/components/setup-hub/SetupHub.vue";
import { useAppConfigStore } from "@/_store/app-config.module.ts";
const environment = import.meta.env.VITE_CLIENT_ENV;

export default defineComponent({
  components: {
    SetupHub,
    NavigationDrawer,
    CoroSnackbar,
    CoroDialog,
    AppBar,
    FloatingGoBackButton,
  },
  setup() {
    const errorsStore = useErrorsStore();
    const snackbarStore = useSnackbarStore();
    const dialogsStore = useDialogsStore();
    const myAccountStore = useMyAccountStore();
    const accountStore = useAccountStore();
    const tutorialStore = useTutorialStore();
    const workspaceStore = useWorkspaceStore();
    const aiAssistantStore = useAiAssistantStore();
    const appsManagementModule = useAppsManagementModule();
    const appConfigStore = useAppConfigStore();
    const gtm = useGtm();
    const { workspace, workspaceAndPermissionsUpdatedTimestamp } = storeToRefs(workspaceStore);
    const { showTrialModeStartedNotice } = storeToRefs(tutorialStore);
    const { handleSocAutomationAfterLogin, handleRedirectFromReportAfterLogin } =
      useRedirectsStore();
    const { closeDialog, openDialog } = dialogsStore;
    const { showDialog, dialogConfig } = storeToRefs(dialogsStore);
    const { error, errors } = storeToRefs(errorsStore);
    const { add } = snackbarStore;
    const { logged, displayLanguage } = storeToRefs(accountStore);
    const { current: vuetifyLocale } = useLocale();
    const workspaceTimestamp = ref(0);
    const route = useRoute();

    // Security gaps navigation
    const { shouldShowGoBackButton, goBackToSecurityGaps, clearNavigationState } =
      useSecurityGapsNavigation();

    const subdomain = computed(() => {
      return extractCoroSubdomain(window.location.hostname);
    });

    const isMspRoute = computed(() => isMspPortalRoute());

    const appTitle = computed(() => {
      if (isMspRoute.value) {
        return i18n.global.t("general.workspacesManagement");
      }
      if (workspace.value.parentWorkspaceName) {
        return workspace.value.parentWorkspaceName;
      }

      if (workspace.value.type === WorkspaceType.CHANNEL) {
        return workspace.value.name;
      }

      return i18n.global.t("general.coroConsole");
    });

    const currentRootRouteName = computed(() => {
      return route.matched[0]?.name as RouteName | undefined;
    });

    watch(subdomain, (newVal) => {
      handleFaviconAndTitle(newVal!);
    });

    watch(
      () => route.name,
      (currentRouteName) => {
        if (currentRouteName === RouteName.DASHBOARD_PAGE && logged.value) {
          identifyChameleon();
        }
        const brandedRoutesNames = [
          RouteName.INVITED_PAGE,
          RouteName.LOGIN,
          RouteName.RESET_PASSWORD,
          RouteName.WORKSPACES,
          RouteName.FORGOT_PASSWORD,
          RouteName.SSO_PAGE,
          RouteName.DOWNLOADS_PAGE,
        ];
        if (brandedRoutesNames.includes(currentRouteName as RouteName)) {
          subdomain.value
            ? workspaceStore.requestBrandingInfo(subdomain.value)
            : workspaceStore.$patch((state) => {
                state.brandingAlias = subdomain.value ?? "";
              });
        }
      }
    );

    watch(
      showTrialModeStartedNotice,
      (value) => {
        if (value) {
          const trialStartedModalConfig = {
            width: ModalWidth.MEDIUM,
            header: { close: false },
            content: {
              component: TrialStartedModal,
            },
            footer: {
              buttons: [
                {
                  title: i18n.global.t(`modals.${TutorialAction.TRIAL_HAS_STARTED}.actionBtn`),
                  color: "primary",
                  spacer: "before",
                  callback: async () => {
                    await tutorialStore.resetTrialNotice();
                    dialogsStore.closeDialog();
                    if (gtm?.enabled()) {
                      window.dataLayer?.push({ event: GtmAction.CONVERT_TO_TRIAL_CLICKED });
                    }
                  },
                  cancel: false,
                },
              ],
            },
            disable: false,
            disclaimer: null,
          };
          const config = dialogsConfigConstructor(trialStartedModalConfig);
          dialogsStore.openDialog(config);
        }
      },
      { immediate: true }
    );

    const openErrorModal = (errorMessage: string) => {
      const errorModal = {
        width: ModalWidth.SMALL,
        header: undefined,
        content: {
          component: ErrorModal,
        },
        footer: null,
        data: {
          item: errorMessage,
        },
      };
      const dialogConfig = dialogsConfigConstructor(errorModal);
      openDialog(dialogConfig);
    };

    const getLoggedUserDataForChameleon = () => {
      const {
        demoModeEndDate,
        demoModeEnabled,
        subscription,
        subscriptionType,
        trialEndDate: planEndDate,
      } = workspace.value;
      const modules = getSelectedModules(subscription);
      const bundles = subscription.bundles;
      const addons = getSelectedAddons(subscription);
      const { type, workspaceId } = workspace.value;
      const demoModeExpirationDate = demoModeEndDate
        ? new Date(demoModeEndDate).toISOString()
        : null;
      const trialEndDate = planEndDate ? new Date(planEndDate).toISOString() : null;
      return {
        workspaceType: type,
        uid: workspaceId,
        workspaceId,
        subscription: {
          modules,
          bundles,
          addons,
        },
        subscriptionType,
        demoModeEnabled,
        demoModeExpirationDate,
        trialEndDate,
      };
    };

    const identifyChameleon = async () => {
      const identity = await appConfigStore.getChameleonIdentity();
      if (!identity) return;
      chmln.clear();
      const { userId, uid_hash } = identity;
      const { workspaceId } = workspace.value;
      const data = workspaceId ? await workspaceStore.getWorkspaceStatsForChameleon() : {};
      chmln.identify(userId, {
        uid_hash,
        environment,
        ...getLoggedUserDataForChameleon(),
        ...data,
      });
    };

    watch(
      () => [...errors.value],
      (newValue, previousValue) => {
        // If error is null or popping errors from store - do nothing
        if (
          !error.value ||
          error.value.customErrorHandling ||
          newValue.length < previousValue.length
        )
          return;
        const errorMessage = i18n.global.te(`errors.${error.value.message}`)
          ? i18n.global.t(`errors.${error.value.message}`)
          : error.value.message;
        const showModal = error.value.showDialog;
        if (showModal) {
          openErrorModal(errorMessage);
        } else {
          const snackbarMessage = {
            html: errorMessage,
            type: SnackbarTypes.ERROR,
          };
          add(snackbarMessage);
        }
      },
      { deep: true }
    );

    // logging out in other open tabs if logout event happened
    window.addEventListener("storage", (event) => {
      if (event.key === "logout-event") {
        accountStore.$reset();
        workspaceStore.$reset();
        aiAssistantStore.$reset();
        window.location.reload();
      }

      if (event.key === "report-navigation") {
        window.location.reload();
      }
    });

    onMounted(async () => {
      if (logged.value && workspace.value.workspaceId) {
        await workspaceStore.getCurrentWorkspace();
        await identifyChameleon();
      }
      handleFaviconAndTitle(subdomain.value!);
    });

    watch(
      () => logged.value,
      async (newVal) => {
        if (newVal) {
          await handleSocAutomationAfterLogin();
          await handleRedirectFromReportAfterLogin();
          await myAccountStore.getProfileData();
        }
      },
      { immediate: true }
    );

    watch(
      () => workspaceAndPermissionsUpdatedTimestamp.value,
      (newVal) => {
        if (newVal) {
          identifyChameleon();
        }
      }
    );

    watch(
      displayLanguage,
      (value, previousValue) => {
        if (value !== previousValue) {
          i18n.global.locale.value = value;
          moment.locale(value);
          vuetifyLocale.value = value;
        }
      },
      { immediate: true }
    );

    // Keep the MSP vs workspace context aligned with the route, including on initial load
    watch(
      currentRootRouteName,
      (newRootName) => {
        if (newRootName) {
          const context =
            newRootName === RouteName.MSP ? AppContext.MSP : AppContext.SINGLE_WORKSPACE;
          appsManagementModule.setContext(context);
        }
      },
      { immediate: true }
    );

    return {
      workspace,
      errors,
      dialogConfig,
      showDialog,
      closeDialog,
      logged,
      workspaceTimestamp,
      isMspRoute,
      appTitle,
      shouldShowGoBackButton,
      goBackToSecurityGaps,
      clearNavigationState,
    };
  },
});
</script>

<style lang="scss">
@use "main";
</style>
